import CustomHead from "@/components/PersonalityV2/CustomHead";
import LandingPage from "@/components/PersonalityV2/LandingPage";
import Questions from "@/components/PersonalityV2/Questions";
import Spinner from "@/components/Spinner";
import Header from "@/components/V2/Navigation/Header";
import { SurveyMasterType } from "@/lib/types/personality";
import { UserType } from "@/lib/types/user";
import { isInAppWebView } from "@/lib/utils/common";
import { sendToApp } from "@/lib/utils/intercom";
import { QuestionType } from "@/lib/utils/mpt";
import {
	ContextType,
	formatQuestions,
	getCampaignVersionAndShareCode,
	getLatestSurveys,
	getSurvey,
	mptMachine,
} from "@/lib/utils/personality";
import { captureException } from "@sentry/nextjs";
import { useMachine } from "@xstate/react";
import { GetServerSideProps } from "next";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import semverGte from "semver/functions/gte";
import GenericErrorMessage from "@/components/V2/GenericErrorMessage/GenericErrorMessage";

type PropsType = {
	totalQuestions: number;
	surveyId: number;
	user?: UserType;
	isLoggedIn: boolean;
	footer: string;
	campaignVersion?: string;
	shareCode?: string;
	survey: SurveyMasterType;
	questionList: QuestionType[];
	err?: boolean;
};

export const getServerSideProps: GetServerSideProps = async (context: any) => {
	let props: PropsType = {
		totalQuestions: 0,
		surveyId: 0,
		footer: "",
		isLoggedIn: false,
		survey: {} as SurveyMasterType,
		questionList: [],
	};

	// const cookie = context?.req?.headers?.cookie;
	// try {
	// 	props.user = await whoAmI({ cookie });
	// 	props.isLoggedIn = true;
	// } catch (error) {}
	try {
		const surveys = await getLatestSurveys();
		const { campaignVersion, shareCode } = getCampaignVersionAndShareCode(
			context.query?.shareCode
		);
		const psychometricSurvey = surveys.find((s) => s.type === 1);
		if (!psychometricSurvey) return { props };

		const surveryResponse = await getSurvey(
			psychometricSurvey.id,
			campaignVersion
		);

		props.survey = surveryResponse;

		props.questionList = formatQuestions(surveryResponse.survey.question_order);

		const totalQuestions = surveryResponse.survey.question_order.filter(
			(q) => q.question.type !== 0
		).length;

		props = {
			...props,
			totalQuestions,
			surveyId: psychometricSurvey.id,
			shareCode: shareCode || "",
			campaignVersion: campaignVersion || "",
		};
	} catch (error) {
		captureException(error);
	}
	return { props };
};

const MoneyPersonality = ({
	surveyId,
	campaignVersion,
	survey,
	questionList,
	shareCode,
	user,
	err,
}: PropsType) => {
	const router = useRouter();
	const [isInsideAppWebView, setIsInsideAppWebView] = useState(false);
	const appVersion = router.query.app_version?.toString();
	const isWebHeaderSupported = appVersion
		? semverGte(appVersion, "2.0.16")
		: false;
	const showInAppHeader = isInsideAppWebView && isWebHeaderSupported;

	const [state, send] = useMachine(mptMachine, {
		context: {
			surveyId,
			campaignVersion,
			routerQuery: router.query,
			questions: questionList,
			shareCode,
			user,
		},
		actions: {
			redirectToResultPage: (ctx: ContextType) => {
				router.push({
					pathname: `/personality/${ctx.submitSurveyIdentifier}/result`,
					query: router.query,
					hash: "top",
				});
			},
		},
	});

	useEffect(() => {
		// to check if the page is opended inside fluter app webview
		setIsInsideAppWebView(isInAppWebView());
	}, []);

	const {
		context: { questions },
	} = state;

	if (err) {
		return <GenericErrorMessage />;
	}

	return (
		<>
			<CustomHead />
			<div className="page_container container pt-7 pb-4">
				{showInAppHeader && (
					<Header
						theme="light"
						onBack={
							isInsideAppWebView
								? () => {
										sendToApp({ action: "go-back" });
								  }
								: undefined
						}
						onHomeClick={
							isInsideAppWebView
								? () => sendToApp({ action: "go-to-home" })
								: undefined
						}
					/>
				)}
				{state.matches("INTRO") && (
					<LandingPage
						title={survey.title}
						subTitle={survey.sub_title}
						image={survey.image_url!}
						ctaText={survey.cta_text}
						isLoading={state.matches({ INTRO: "STARTING_SURVEY" })}
						onStart={() => send("START")}
					/>
				)}
				{state.matches("TAKE_SURVEY") && (
					<Questions
						questions={questions}
						isSubmitting={state.matches({ TAKE_SURVEY: "SUBMITTING" })}
						onAnswer={(id, answer) =>
							send({ type: "UPDATE_ANSWER", data: { id, answer } })
						}
					/>
				)}
				{state.matches("RESULT") && (
					<div className="flex justify-content-center align-items-center mt-10">
						<Spinner color="var(--primary-500)" />
					</div>
				)}
			</div>
			<style jsx>{`
				.container {
					display: flex;
					flex-direction: column;
					height: 100%;
				}
			`}</style>
		</>
	);
};

export default MoneyPersonality;
